import { getDateWithTimeZone, getTimeWithTimeZone, getTimezoneOffset } from '@/helpers';
import { mergeClasses } from '@/utils';

import { Lightning01Icon, Loading02Icon, Mail02Icon } from '@/assets/icons';
import {
  Button,
  DashedArrow,
  SvgIcon,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/common';

interface IIntakeScheduleCard {
  className?: string;
  title?: string;
  email?: string;
  lastJobTime?: Date;
  nextJobTime?: Date;
  onTrigger: () => void;
  isRunning: boolean;
}

export const IntakeScheduleCard = ({
  className,
  title,
  email,
  lastJobTime,
  nextJobTime,
  onTrigger,
  isRunning = false,
}: IIntakeScheduleCard) => {
  return (
    <div className={mergeClasses('glass-effect p-4 shrink-0', className)}>
      <div className="flex flex-row justify-between items-center w-full gap-5">
        <div className="flex flex-col justify-start items-start">
          <span className="text-h5 font-bold text-neutral-9 uppercase shrink-0">
            {title ? title : <span className="tracking-[1px] font-bold text-h5">--</span>}
          </span>
          <div className="flex flex-row justify-start items-center gap-2 min-w-0">
            <Mail02Icon className="size-4 text-neutral-9 shrink-0" />
            {email ? (
              <Tooltip>
                <TooltipTrigger
                  title={email}
                  className="block min-w-0 max-w-[260px] truncate text-left font-medium text-body-sm text-neutral-7 cursor-default"
                >
                  {email}ajhsjdhsajdhsajdhasjdsa
                </TooltipTrigger>
                <TooltipContent
                  className="text-body-sm font-medium rounded-xs"
                  size="lg"
                  side="bottom"
                >
                  {email}
                </TooltipContent>
              </Tooltip>
            ) : (
              <span className="tracking-[1px] font-medium text-body-sm">--</span>
            )}
          </div>
        </div>
        <Button
          buttonColor="primary"
          buttonStyle="filled"
          borderRadius="soft"
          size="lg"
          onClick={onTrigger}
          disabled={isRunning}
          className="shrink-0"
        >
          <SvgIcon
            component={isRunning ? Loading02Icon : Lightning01Icon}
            className={mergeClasses('size-6', isRunning && 'animate-[spin_2s_linear_infinite]')}
          />
          {isRunning ? 'Job Running...' : 'Run Intake Now'}
        </Button>
      </div>
      <div className="flex flex-col justify-between items-start gap-1 mt-2 w-full">
        <div className="flex flex-row justify-between items-center w-full">
          <span className="font-semibold text-body-xs text-neutral-7 uppercase">Last Job</span>
          <span className="font-semibold text-body-xs text-neutral-7 uppercase">Next Job</span>
        </div>
        <div className="flex flex-row justify-between items-center w-full">
          <span className="font-medium text-body-xs text-neutral-7">
            {lastJobTime ? (
              getDateWithTimeZone(lastJobTime)
            ) : (
              <span className="tracking-[1px] font-medium text-body-xs">--</span>
            )}
          </span>
          <span className="font-medium text-body-xs text-neutral-7">
            {nextJobTime ? (
              getDateWithTimeZone(nextJobTime)
            ) : (
              <span className="tracking-[1px] font-medium text-body-xs">--</span>
            )}
          </span>
        </div>
        <div className="flex flex-row gap-1 w-full">
          <span className="font-bold text-h6 text-neutral-9 shrink-0">
            {lastJobTime ? (
              getTimeWithTimeZone(lastJobTime)
            ) : (
              <span className="tracking-[1px] font-bold text-h6">--:--</span>
            )}
          </span>
          <DashedArrow />
          <span className="font-bold text-h6 text-neutral-9 shrink-0">
            {nextJobTime ? (
              getTimeWithTimeZone(nextJobTime)
            ) : (
              <span className="tracking-[1px] font-bold text-h6">--:--</span>
            )}
          </span>
        </div>
        {(lastJobTime || nextJobTime) && (
          <div className="flex flex-row justify-between items-center w-full">
            {lastJobTime && (
              <span className="font-medium text-body-xs text-neutral-7 uppercase">
                ({getTimezoneOffset(lastJobTime)})
              </span>
            )}
            {nextJobTime && (
              <span className="font-medium text-body-xs text-neutral-7 uppercase">
                ({getTimezoneOffset(nextJobTime)})
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
